/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { jsx, css } from '@emotion/react'
import { Trans, Translation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from "react-router-dom"
import { filter, unionBy, size, get, head, map } from 'lodash'
import { DropdownStandalone } from './form/Dropdown'
import { RadioGroupFieldStandalone } from './form/RadioGroupFieldStandalone'
import Card from './layout/Card'
import { upgradePathList } from '../actions/upgrade_path'
import { SubscriptionOptionWithExpiryDate } from './SubscriptionOptionWithExpiryDate'
import NestedWrappingBusyMask from './NestedWrappingBusyMask'
import { globalSettingsList } from '../actions/settings'
import * as Yup from 'yup'
import MainLayout from './MainLayout'
import { Row, Col, Container, Popover, OverlayTrigger, Button } from 'react-bootstrap'
import { CardHeader } from './layout/CardHeader'
import { Separator } from './layout/Separator'
import { customerSubscriptionUpgrade, customerSubscriptionList } from '../actions/customer_subscription'
import { SubscriptionUpgradeCard } from './SubscriptionUpgradeCard'

const PRODUCT_TYPE_OPTIONS = [ {value: 'voice', label: 'Voice subscription'},
                               {value: 'fax', label: 'Fax subscription'},
                             ]

export const validationSchema = Yup.object().shape({
})

export const SubscriptionUpgrade = ({...props}) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const { subscription_id } = useParams()
    const subscription = useSelector(() => subscription_id && customerSubscriptionList.getObject(subscription_id))
    const subscriptions = useSelector(() => unionBy(customerSubscriptionUpgrade.getObjects(), [subscription], (sub) => sub.id))
    const isLoading = useSelector(() => customerSubscriptionUpgrade.isLoading())
    const [productType, setProductType] = useState('fax')
    const initial_values = {}
    const helpdesk_email = useSelector(() => globalSettingsList.getSetting("helpdesk_email"))

    useEffect(() => {
        dispatch(upgradePathList.filterCustomerSubscriptionListForUpgrades({customerSubscriptionItemList:customerSubscriptionUpgrade}))
        dispatch(globalSettingsList.ensureGlobalSettingsLoaded())
    }, [])
    
    useEffect(() => {
        if ( subscription_id ) {
            dispatch(customerSubscriptionList.ensureObjectLoaded(subscription_id))
        } else if ( size(subscriptions) > 0 ) {
            const defaultSubscription = head(subscriptions)
            history.push(`/subscription_upgrade/${defaultSubscription.id}`)
        }
    }, [subscription_id])

    useEffect(() => {
        if ( get(subscription, "product_can_receive_voice") ) {
            setProductType("voice")
        } else {
            setProductType("fax")
        }
    }, [get(subscription, "id")])
    
    const onChangeSelectedSubscription = (new_subscription_id) => {
        const subscription = customerSubscriptionUpgrade.getObject(new_subscription_id)
        history.push(`/subscription_upgrade/${new_subscription_id}`)
    }
    
    const renderYourSubscription = () => {
        const subscriptions_as_options = map(subscriptions, function(subscription)  {
            return { value: subscription.id,
                     label: <SubscriptionOptionWithExpiryDate subscription={subscription} />
                   }
        })
        
        return (
            <div>
              <h2>
                <Trans>Your subscription</Trans>
              </h2>

              <div css={subscription_dropdown_container_style}>
                <NestedWrappingBusyMask is_loading={!subscription}>
                    { subscription && size(subscriptions_as_options) > 0 && 
                    <DropdownStandalone options={subscriptions_as_options}
                                        default_value={get(subscription, "id")}
                                        on_change={onChangeSelectedSubscription}
                    />
                  }
                </NestedWrappingBusyMask>
              </div>
              
            </div>
        )
    }

    const renderAvailableProducts = () => {
        const productFilter = {}
        let options = PRODUCT_TYPE_OPTIONS
        if ( productType === 'fax' ) {
            productFilter.can_receive_faxes = subscription.product_can_receive_faxes
            productFilter.can_receive_voice = null

            if ( ! productFilter.can_receive_faxes ) {
                options = filter(options, (x) => x.value == 'fax')
            }
            
        } else {
            productFilter.can_receive_voice = true
            productFilter.can_receive_faxes = null
        }
        
        return (
            <>

              <h2>
                <Trans>Change to</Trans>
              </h2>
              
              <RadioGroupFieldStandalone value={productType}
                                         options={options}
                                         onChange={setProductType} />

              <Separator variant="h20" />
              
              <SubscriptionUpgradeCard product_filter={productFilter}
                                       subscription={subscription}
                                       force_phone_number={subscription.phone_number}
                                       additional_create_values={{upgrade_subscription: subscription_id}} />
            </>
        )
    }

    const renderNoSubscriptionsToUpgrade = () => {
        return (
            <div>
              <Trans i18nKey="upgrade_path_no_subs_to_upgrade"
                     values={{ helpdesk_email: helpdesk_email }}
                     components={[<a href={`mailto:${helpdesk_email}`} >{helpdesk_email}</a>]}
                     defaults="You do not have any subscriptions that can be upgraded at the moment. If you want to re-activate a recently expired subscription please contact the XOIP helpdesk at <0>{{helpdesk_email}}</0>."
              />
            </div>
        )
    }
    
    return (
        <MainLayout active_key="subscription_upgrade"
                    breadcrumbs={[{name: 'home'},
                                  {name: 'subscription_upgrade', label: 'Subscription Upgrade', url: '/subscription_upgrade'}]}>
          <Container fluid>

            <Separator variant="h20" />

            <Row>
              <Col md={12}>
                <CardHeader title="Change your subscription" />
              </Col>
            </Row>

            <Card variant="white_wide_and_top_padding" with_padding_below={false}>

              { ! isLoading && size(subscriptions) === 0 && renderNoSubscriptionsToUpgrade() }

              { size(subscriptions) > 0 && 
                <NestedWrappingBusyMask isLoading={isLoading}>
                  {renderYourSubscription()}

                  <Separator variant="h10" />
                  
                  {renderAvailableProducts()}
                </NestedWrappingBusyMask>
              }
            </Card>
            
          </Container>
        </MainLayout>

    )
    
}


const subscription_dropdown_container_style = css`
max-width: 400px;
`
